/**
 * AP-32 Nachtrag — **Die Felder, die nicht jede Belegart führt.**
 *
 * Lieferkonditionen, Liefertermin, Preisgültigkeit, Angebotsfrist,
 * Leistungszeitraum und Zahlungstermine. Welche erscheinen, entscheidet
 * `hatKopffeld` — dieselbe Tabelle, die der Server beim Speichern fragt. Die
 * Texte, die auf dem Beleg stehen werden, rechnen dieselben Formatierer wie das
 * PDF (`lieferterminText`, `zahlungsplanBetraege`).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import {
  DELIVERY_DATE_TYPES,
  INCOTERMS,
  MAX_PAYMENT_SCHEDULE_ENTRIES,
  drittelPlan,
  ganzerMonat,
  hatKopffeld,
  isoWoche,
  lieferkonditionText,
  lieferterminText,
  monatsGrenzen,
  montagDerWoche,
  pruefeZahlungsplan,
  zahlungsplanBetraege,
  type DeliveryDateType,
  type DocumentKind,
  type PaymentScheduleEntry,
} from '@symphenia/shared-types'
import { formatBetrag, leseZahl, zahlText } from '../lib/grid'

/** Ein Zahlungstermin, wie er getippt wird — der Prozentsatz als Text, leer = Rest. */
export interface PlanZeile {
  text: string
  percent: string
}

export interface KindFieldValues {
  incoterm: string
  incotermPlace: string
  deliveryTermsText: string
  deliveryDateType: string
  deliveryDate: string
  deliveryDateTo: string
  deliveryNote: string
  validUntil: string
  quoteDeadline: string
  serviceDateFrom: string
  serviceDateTo: string
  paymentSchedule: PlanZeile[]
}

export function planAusAnsicht(plan: readonly PaymentScheduleEntry[] | null | undefined): PlanZeile[] {
  return (plan ?? []).map((e) => ({ text: e.text, percent: e.percent === null ? '' : zahlText(e.percent, 2) }))
}

export function planZuEingabe(plan: readonly PlanZeile[]): PaymentScheduleEntry[] {
  return plan
    .filter((z) => z.text.trim() || z.percent.trim())
    .map((z) => {
      const p = leseZahl(z.percent)
      return { text: z.text.trim(), percent: p === null || Number.isNaN(p) ? null : p }
    })
}

interface Props {
  kind: DocumentKind
  werte: KindFieldValues
  setze: (patch: Partial<KindFieldValues>) => void
  editierbar: boolean
  sprache: string
  waehrung: string
  /** Gesamtbetrag in Cent für die Beträge der Zahlungstermine — `null` ohne Preisrecht. */
  gesamtCent: number | null
  eingabe: string
  label: string
}

const MONATE = Array.from({ length: 12 }, (_, i) => i + 1)

export function KindFields({ kind, werte, setze, editierbar, sprache, waehrung, gesamtCent, eingabe, label }: Props) {
  const { t } = useTranslation()
  const lang = sprache === 'en' ? 'en' : 'de'
  const hat = (f: Parameters<typeof hatKopffeld>[1]) => hatKopffeld(kind, f)

  if (!['deliveryTerms', 'deliveryDate', 'priceValidity', 'quoteDeadline', 'servicePeriod', 'paymentSchedule'].some((f) => hat(f as never))) {
    return null
  }

  return (
    // Nachtrag 5: steht jetzt oben in „Konditionen" — die Trennlinie gehört darunter.
    <div className="mb-4 grid gap-4 border-b border-border pb-4 lg:grid-cols-2">
      {hat('servicePeriod') && <Leistungszeitraum werte={werte} setze={setze} editierbar={editierbar} eingabe={eingabe} label={label} />}

      {(hat('priceValidity') || hat('quoteDeadline')) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {hat('priceValidity') && (
            <div>
              <label className={label}>{t('documents.head.validUntil')}</label>
              <input type="date" disabled={!editierbar} value={werte.validUntil} onChange={(e) => setze({ validUntil: e.target.value })} className={eingabe} />
            </div>
          )}
          {hat('quoteDeadline') && (
            <div>
              <label className={label}>{t('documents.head.quoteDeadline')}</label>
              <input type="date" disabled={!editierbar} value={werte.quoteDeadline} onChange={(e) => setze({ quoteDeadline: e.target.value })} className={eingabe} />
            </div>
          )}
        </div>
      )}

      {hat('deliveryTerms') && (
        <div>
          <p className={label}>{t('documents.head.deliveryTerms')}</p>
          <div className="grid gap-2 sm:grid-cols-[9rem_1fr]">
            <select disabled={!editierbar} value={werte.incoterm} onChange={(e) => setze({ incoterm: e.target.value })} className={eingabe} title={t('documents.head.incoterm')}>
              <option value="">{t('documents.head.incotermNone')}</option>
              {INCOTERMS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <input disabled={!editierbar || !werte.incoterm} value={werte.incotermPlace} onChange={(e) => setze({ incotermPlace: e.target.value })} className={eingabe} placeholder={t('documents.head.incotermPlace')} />
          </div>
          <input
            disabled={!editierbar}
            value={werte.deliveryTermsText}
            onChange={(e) => setze({ deliveryTermsText: e.target.value })}
            className={`${eingabe} mt-2`}
            placeholder={t('documents.head.deliveryTermsTextPlaceholder')}
          />
          <Vorschau text={lieferkonditionText(werte.incoterm || null, werte.incotermPlace, werte.deliveryTermsText)} />
        </div>
      )}

      {hat('deliveryDate') && <Liefertermin werte={werte} setze={setze} editierbar={editierbar} eingabe={eingabe} label={label} sprache={lang} />}

      {hat('paymentSchedule') && (
        <Zahlungstermine
          plan={werte.paymentSchedule}
          setze={(paymentSchedule) => setze({ paymentSchedule })}
          editierbar={editierbar}
          sprache={lang}
          waehrung={waehrung}
          gesamtCent={gesamtCent}
          eingabe={eingabe}
          label={label}
        />
      )}
    </div>
  )
}

function Vorschau({ text }: { text: string | null }) {
  const { t } = useTranslation()
  return text ? <p className="mt-1 text-xs text-muted-foreground">{t('documents.head.onDocument', { text })}</p> : null
}

// ── Leistungszeitraum (N-23) ─────────────────────────────────────────────────

function Leistungszeitraum({ werte, setze, editierbar, eingabe, label }: Pick<Props, 'werte' | 'setze' | 'editierbar' | 'eingabe' | 'label'>) {
  const { t } = useTranslation()
  const monat = ganzerMonat(werte.serviceDateFrom, werte.serviceDateTo)
  const [modus, setModus] = useState<'month' | 'range'>(monat || (!werte.serviceDateFrom && !werte.serviceDateTo) ? 'month' : 'range')
  const jetzt = new Date()
  const [jahr, mon] = (monat ?? `${jetzt.getFullYear()}-${String(jetzt.getMonth() + 1).padStart(2, '0')}`).split('-').map(Number)
  const setzeMonat = (j: number, m: number) => {
    const g = monatsGrenzen(`${j}-${String(m).padStart(2, '0')}`)
    setze({ serviceDateFrom: g.von, serviceDateTo: g.bis })
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className={label}>{t('documents.head.servicePeriod')}</p>
        <div className="mb-1 inline-flex overflow-hidden rounded-md border border-input text-xs">
          {(['month', 'range'] as const).map((m) => (
            <button
              key={m}
              type="button"
              disabled={!editierbar}
              onClick={() => setModus(m)}
              className={`px-2 py-0.5 ${modus === m ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              {t(m === 'month' ? 'documents.head.servicePeriodMonth' : 'documents.head.servicePeriodRange')}
            </button>
          ))}
        </div>
      </div>
      {modus === 'month' ? (
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          <select disabled={!editierbar} value={monat ? mon : ''} onChange={(e) => e.target.value && setzeMonat(jahr, Number(e.target.value))} className={eingabe} aria-label={t('documents.head.month')}>
            <option value="">—</option>
            {MONATE.map((m) => (
              <option key={m} value={m}>
                {new Date(Date.UTC(2026, m - 1, 1)).toLocaleDateString(navigator.language, { month: 'long', timeZone: 'UTC' })}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={2000}
            max={2100}
            disabled={!editierbar}
            value={jahr}
            onChange={(e) => {
              const j = Number(e.target.value)
              if (j >= 2000 && j <= 2100) setzeMonat(j, monat ? mon : jetzt.getMonth() + 1)
            }}
            className={eingabe}
            aria-label={t('documents.head.year')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input type="date" disabled={!editierbar} value={werte.serviceDateFrom} onChange={(e) => setze({ serviceDateFrom: e.target.value })} className={eingabe} aria-label={t('documents.fields.serviceDateFrom')} />
          <input type="date" disabled={!editierbar} value={werte.serviceDateTo} onChange={(e) => setze({ serviceDateTo: e.target.value })} className={eingabe} aria-label={t('documents.fields.serviceDateTo')} />
        </div>
      )}
    </div>
  )
}

// ── Liefertermin (N-20) ──────────────────────────────────────────────────────

function Liefertermin({ werte, setze, editierbar, eingabe, label, sprache }: Pick<Props, 'werte' | 'setze' | 'editierbar' | 'eingabe' | 'label'> & { sprache: 'de' | 'en' }) {
  const { t } = useTranslation()
  const typ = werte.deliveryDateType as DeliveryDateType | ''
  const woche = werte.deliveryDate ? isoWoche(werte.deliveryDate) : null
  const heute = new Date()
  const setzeWoche = (w: number, j: number) => {
    if (w >= 1 && w <= 53 && j >= 2000 && j <= 2100) setze({ deliveryDate: montagDerWoche(w, j) })
  }
  const text = lieferterminText({ type: typ || null, date: werte.deliveryDate || null, dateTo: werte.deliveryDateTo || null, note: werte.deliveryNote }, sprache)

  return (
    <div>
      <p className={label}>{t('documents.head.deliveryDate')}</p>
      <div className="grid gap-2 sm:grid-cols-[11rem_1fr]">
        {/* Nachtrag 5: Das Datum steht immer da — ohne Zusatz gilt es wie „am" (gespeichert als DATE). */}
        <select disabled={!editierbar} value={typ === 'DATE' ? '' : typ} onChange={(e) => setze({ deliveryDateType: e.target.value })} className={eingabe}>
          <option value="">{t('documents.head.deliveryDateTypes.NONE')}</option>
          {DELIVERY_DATE_TYPES.filter((d) => d !== 'DATE').map((d) => (
            <option key={d} value={d}>
              {t(`documents.head.deliveryDateTypes.${d}`)}
            </option>
          ))}
        </select>
        {(typ === '' || typ === 'DATE' || typ === 'UNTIL' || typ === 'APPROX' || typ === 'FIXED') && (
          <input type="date" disabled={!editierbar} value={werte.deliveryDate} onChange={(e) => setze({ deliveryDate: e.target.value })} className={eingabe} />
        )}
        {typ === 'PERIOD' && (
          <div className="grid grid-cols-2 gap-2">
            <input type="date" disabled={!editierbar} value={werte.deliveryDate} onChange={(e) => setze({ deliveryDate: e.target.value })} className={eingabe} />
            <input type="date" disabled={!editierbar} value={werte.deliveryDateTo} onChange={(e) => setze({ deliveryDateTo: e.target.value })} className={eingabe} />
          </div>
        )}
        {typ === 'WEEK' && (
          // Zwei Zahlenfelder statt `<input type="week">` — den kennt Firefox nicht.
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min={1}
              max={53}
              disabled={!editierbar}
              value={woche?.woche ?? ''}
              onChange={(e) => setzeWoche(Number(e.target.value), woche?.jahr ?? heute.getFullYear())}
              className={eingabe}
              placeholder={t('documents.head.week')}
              aria-label={t('documents.head.week')}
            />
            <input
              type="number"
              min={2000}
              max={2100}
              disabled={!editierbar}
              value={woche?.jahr ?? heute.getFullYear()}
              onChange={(e) => setzeWoche(woche?.woche ?? 1, Number(e.target.value))}
              className={eingabe}
              aria-label={t('documents.head.year')}
            />
          </div>
        )}
      </div>
      {typ && (
        <input
          disabled={!editierbar}
          value={werte.deliveryNote}
          onChange={(e) => setze({ deliveryNote: e.target.value })}
          className={`${eingabe} mt-2`}
          placeholder={typ === 'TEXT' ? t('documents.head.deliveryNoteTextPlaceholder') : t('documents.head.deliveryNote')}
        />
      )}
      <Vorschau text={text} />
    </div>
  )
}

// ── Zahlungstermine (N-22) ───────────────────────────────────────────────────

function Zahlungstermine({
  plan,
  setze,
  editierbar,
  sprache,
  waehrung,
  gesamtCent,
  eingabe,
  label,
}: {
  plan: PlanZeile[]
  setze: (plan: PlanZeile[]) => void
  editierbar: boolean
  sprache: 'de' | 'en'
  waehrung: string
  gesamtCent: number | null
  eingabe: string
  label: string
}) {
  const { t } = useTranslation()
  const eintraege = planZuEingabe(plan)
  const befund = pruefeZahlungsplan(eintraege)
  const betraege = gesamtCent === null ? [] : zahlungsplanBetraege(eintraege, gesamtCent)
  const summe = eintraege.reduce((s, e) => s + (e.percent ?? 0), 0)
  const aendere = (i: number, patch: Partial<PlanZeile>) => setze(plan.map((z, j) => (j === i ? { ...z, ...patch } : z)))

  return (
    <div className="lg:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={label}>{t('documents.head.paymentSchedule')}</p>
        {editierbar && (
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => setze(planAusAnsicht(drittelPlan(sprache)))} className="rounded border border-input px-2 py-0.5 hover:bg-muted">
              {t('documents.head.thirds')}
            </button>
            <button
              type="button"
              disabled={plan.length >= MAX_PAYMENT_SCHEDULE_ENTRIES}
              onClick={() => setze([...plan, { text: '', percent: '' }])}
              className="rounded border border-input px-2 py-0.5 hover:bg-muted disabled:opacity-50"
            >
              {t('documents.head.addInstalment')}
            </button>
          </div>
        )}
      </div>
      {plan.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {plan.map((z, i) => (
              <tr key={i}>
                <td className="w-24 py-1 pr-2">
                  <input
                    disabled={!editierbar}
                    value={z.percent}
                    onChange={(e) => aendere(i, { percent: e.target.value })}
                    className={`${eingabe} text-right`}
                    inputMode="decimal"
                    placeholder={i === plan.length - 1 ? t('documents.head.rest') : '%'}
                  />
                </td>
                <td className="py-1 pr-2">
                  <input disabled={!editierbar} value={z.text} onChange={(e) => aendere(i, { text: e.target.value })} className={eingabe} placeholder={t('documents.head.instalmentText')} maxLength={200} />
                </td>
                <td className="w-32 py-1 pr-2 text-right tabular-nums text-muted-foreground">
                  {gesamtCent !== null && befund !== 'percentInvalid' && betraege[i] !== undefined ? formatBetrag(betraege[i] / 100, waehrung) : ''}
                </td>
                <td className="w-8 py-1 text-right">
                  {editierbar && (
                    <button type="button" onClick={() => setze(plan.filter((_, j) => j !== i))} className="p-1 text-muted-foreground hover:text-destructive" title={t('documents.head.removeInstalment')}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className={`mt-1 text-xs ${befund === 'ok' ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}>
        {plan.length > 0 && befund !== 'ok' ? t(`documents.paymentSchedule.${befund}`) : t('documents.head.paymentScheduleHint')}
        {plan.length > 0 && ` · ${t('documents.head.scheduleSum', { percent: zahlText(summe, 2) })}`}
      </p>
    </div>
  )
}
